import { Component, OnInit, HostListener, Inject, AfterViewInit } from "@angular/core";
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { NgxSpinnerService } from "ngx-spinner";
import { AuthService } from "../../share/service/auth.service";
import { UserDataService } from "src/app/share/data-service/user.data.service";
import { Router } from "@angular/router";
import { timer, Subscription, firstValueFrom } from 'rxjs';
import { AccountLockService } from 'src/app/share/service/password-services/account-lock.service'
import { PasswordPolicyDto } from 'src/app/share/interface/password.interface';
import { PasswordPolicyService } from "src/app/share/service/password-services/password-policy.service";
import { MatDialog } from '@angular/material/dialog';
import { PasswordExpiryDialogComponent } from 'src/app/share/dialog/password-expiry-dialog/password-expiry-dialog.component';
import { PasswordCheckService } from "src/app/share/service/password-services/password-check.service";
import { AlertService } from "src/app/share/service/alert.service"
import { AuthenticationTokenService } from "../../share/service/password-services/authentication-token-service";
import { environment } from 'src/environments/environment';
import { NgZone } from '@angular/core';
declare const google: any;
import { UserService } from "src/app/share/service/user.service";

@Component({
	selector: "app-login",
	templateUrl: "./login.component.html",
	styleUrls: ["./login.component.scss"]

})
export class LoginComponent {
	formGroup: UntypedFormGroup;
	hide: boolean = true;

	// Take in from back end
	lockDuration: number | null; // In minutes
	lastUpdate: Date = new Date();
	rMLogin: boolean = false;
	googleLogin: boolean = false;
	passwordLogin: boolean = true;

	// Lock attributed variables
	failureSetting: number = 5;
	failureCount: number = 0;
	accountLocked: boolean = false;
	remainingAttempts: number = this.failureSetting
	unlockTime: Date | null = null;
	countdown: string = '';
	private timerSubscription: Subscription | null = null;

	// Password Update attributed variables
	passwordExpiryDays: number = 0;

	userId: string | null = null;
	userEmail: string | null = null;

	constructor(
		private authService: AuthService,
		private userService: UserService,
		private userDataService: UserDataService,
		private passwordCheckService: PasswordCheckService,
		private ngxSpinnerService: NgxSpinnerService,
		private alertService: AlertService,
		private dialog: MatDialog,
		private router: Router,
		private accountLockService: AccountLockService,
		private passwordPolicyService: PasswordPolicyService,
		private authenticationTokenService: AuthenticationTokenService,
		private ngZone: NgZone,
	) { }

	ngOnInit(): void {
		this.formGroup = new FormGroup({
			email: new FormControl(null, [Validators.required, Validators.email]),
			password: new FormControl(null, [Validators.required]),
			rememberMe: new FormControl(false)
		});

		this.initializeLockDuration();
	}

	ngAfterViewInit(): void {

		setTimeout(() => {

			const element = document.getElementById('googleSignInButton');

			google.accounts.id.initialize({
				client_id: environment.googleClientId,
				callback: async (response) => {
					this.ngZone.run(async () => {
						const result = await this.authenticationTokenService.handleGoogleResponse(response);

						console.log("[Google Login] Google Account Validated")

						if (!result) {
							console.log("[Google Login] JWT failed")
						}

						const isLocked = await this.checkAccountLock(result.email);
						if (isLocked) return;

						this.loginByGoogle(result.token);
					})
				}
			});
		}, 0);
	}

	ngOnDestroy(): void {
		this.clearTimer();
	}

	// Get the lock duration from the PasswordPolicies
	private initializeLockDuration(): void {
		this.passwordPolicyService.getActivePolicy().subscribe({
			next: (policy: PasswordPolicyDto) => {
				this.lockDuration = policy.lockDuration;
				this.rMLogin = policy.rememberMeLogin;
				this.googleLogin = policy.googleLogin;
				this.passwordLogin = policy.passwordLogin;

				if (this.googleLogin) {
					setTimeout(() => {
						this.renderGoogleButton();
					})
				}
				// Check cookie on initialization if enabled
				// Does not check if login method is Google SSO only (even if )
				if (this.rMLogin && !(this.googleLogin &&  !this.passwordLogin)) {
					this.rememberMeLogin();
				}
			},
			error: (err) => {
				console.error('Failed to load password policies', err);

				this.lockDuration = 15;
				this.rMLogin = false;
				this.googleLogin = false;
			}
		});
	}

	private renderGoogleButton() {
		google.accounts.id.renderButton(
			document.getElementById('googleSignInButton'),
			{
				theme: 'outline',
				size: 'large',
				width: '370'
			}
		);
	}

	async login(): Promise<void> {
		if (this.formGroup.invalid || !this.passwordLogin) return;

		const value = this.formGroup.value;
		this.ngxSpinnerService.show();
		this.userEmail = value.email;

		try {
			const accountLocked = await this.checkAccountLock(value.email);
			if (accountLocked) return;

			const token = await this.userDataService.login(value.email, value.password);
			this.authService.setToken(token);

			const profile = await this.getAccountInfo();

			this.ngxSpinnerService.hide();

			this.checkPasswordAge();
		} catch (err: any) {
			this.handleFailedLogin(value.email);
			this.alertService.openSnackBar(err?.error || "Login failed", "DISMISS");
		}
	}

	// Checks password age and decides if the user needs to update password or not
	private async checkPasswordAge(): Promise<void> {
		const isPasswordRecent = await this.passwordCheckService.checkPasswordAge(this.userId);

		if (!isPasswordRecent) {
			const passwordUpdated = await this.showPasswordUpdateDialog();

			if (!passwordUpdated) {
				return;
			}
		}

		if (this.formGroup.value.rememberMe) {
			await this.createRememberMeToken();
		}

		this.router.navigate(['/settings/myaccount']);
	}

	// Shows the update dialog
	private async showPasswordUpdateDialog(): Promise<boolean> {
		try {
			const userProfile = await this.userDataService.getProfile();

			const dialogRef = this.dialog.open(PasswordExpiryDialogComponent, {
				width: '450px',
				maxWidth: '90vw',
				panelClass: 'centered-dialog',
				disableClose: true,
				data: { user: userProfile }
			});

			// Returns true if password successfully updated
			// Returns false if cancelled or not updated
			const result = await firstValueFrom(dialogRef.afterClosed());

			if (result?.success) return true;

			this.authService.logout();
			location.reload();

			return false;
		}
		catch (err) {
			this.authService.logout();
			location.reload();

			return false;
		}
	}

	@HostListener('window:beforeunload', ['$event'])
	beforeUnloadHandler(event: Event) {
		if (this.dialog.openDialogs.length > 0) {
			event.preventDefault();
			(event as BeforeUnloadEvent).returnValue = 'You have unsaved changes in the password dialog';
		}
	}

	private async checkAccountLock(email: string): Promise<boolean> {
		this.userEmail = email;
		const lockStatus = await this.accountLockService.getLockStatusByEmail(email);

		if (lockStatus.lockType === 'Temporary' && this.lockDuration !== null) {
			this.accountLocked = true;
			this.unlockTime = new Date(lockStatus.unlockTime!);
			this.startCountdown();
			this.ngxSpinnerService.hide();
			this.alertService.openSnackBar("Account temporarily locked.");
			return true;
		}

		if (lockStatus.lockType === 'Permanent' && this.lockDuration !== null) {
			this.accountLocked = true;
			this.ngxSpinnerService.hide();
			this.alertService.openSnackBar("Account permanently locked.");
			return true;
		}

		return false;
	}  

	// Counts up the number of login attempts and the number of remaining attempts
	handleFailedLogin(email: string): void {

		if (this.lockDuration === null) {
			return;
		}

		this.failureCount++;

		const remaining = this.failureSetting - this.failureCount;
		this.remainingAttempts = remaining;

		if (remaining <= 0) {
			// Get the current locktime policy
			const lockDuration = this.lockDuration;

			this.accountLockService.addTempLock(email, lockDuration).subscribe({
				next: () => {
					this.accountLocked = true;
					this.unlockTime = new Date(Date.now() + lockDuration * 60000);
					this.startCountdown();
					this.alertService.openSnackBar('Account temporarily locked due to failed attempts.', 'DISMISS');
				},
				error: () => {
					this.alertService.openSnackBar('Failed to apply lock. Please contact support.', 'DISMISS');
				},
			});
		} else {
			this.alertService.openSnackBar(`Login failed. ${remaining} attempts remaining.`, 'DISMISS');
		}
	}

	// Account lock countdown
	private startCountdown(): void {
		this.clearTimer();

		this.timerSubscription = timer(0, 1000).subscribe(() => {
			if (!this.unlockTime) {
				this.clearTimer();
				return;
			}

			const now = new Date();
			const diffMs = this.unlockTime.getTime() - now.getTime();

			if (diffMs <= 0) {
				this.resetLoginAttempts();
				return;
			}

			const diffMins = Math.floor(diffMs / 60000);
			const diffSecs = Math.floor((diffMs % 60000) / 1000);

			this.countdown = `${diffMins}:${diffSecs < 10 ? '0' + diffSecs : diffSecs}`;
		});
	}

	// Account lock timer reset
	private clearTimer() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
			this.timerSubscription = null;
		}
	}

	// Resets login attempts
	private async resetLoginAttempts(): Promise<void> {
		try {
			const lockStatus = await this.accountLockService.getLockStatusByEmail(this.userEmail);

			if (lockStatus.lockType === 'None') {
				this.failureCount = 0;
				this.remainingAttempts = this.failureSetting;
				this.accountLocked = false;
				this.unlockTime = null;
				this.countdown = '';
				this.clearTimer();
			} else if (lockStatus.lockType === 'Temporary') {
				// Lock still active
				this.unlockTime = new Date(lockStatus.unlockTime!);
				this.startCountdown();
			}
		} catch (err) {
			this.alertService.openSnackBar("Failed to refresh lock status", "DISMISS");
		}
	}

	private async createRememberMeToken(): Promise<void> {
		try {
			const response = await this.authenticationTokenService.createToken({
				userId: this.userId!,
				userAgent: navigator.userAgent
			});

			if (!response) {
				console.error('[Login] Token creation failed');
				return;
			}

			console.log('[Login] Remember Me token received');
		}
		catch (err) {
			console.error("Failed to create remember-me token", err);
		}
	}

	private async rememberMeLogin(): Promise<void> {
		try {
			const response = await this.authenticationTokenService.rotateToken();

			if (!response) {
				console.log('[Login] Token validation request failed');
				return;
			}
			if (!response.success) {
				console.log(response.message ?? '[Login] No valid remember-me token found');
				return
			}

			console.log('[Login] Remember-me token validated and rotated')

			// Obtain JWT/session from backend
			this.authService.setToken(response.token);

			const profile = await this.getAccountInfo();

			// Check password before logging in
			this.checkPasswordAge();
		}
		catch (err) {
			console.error('[Login] Remember-me login failed', err);
		}
	}

	// Gets account info for login functions
	private async getAccountInfo(): Promise<any> {
		console.log('[Account Info] GetProfile');

		const profile = await this.userDataService.getProfile();
		this.userId = profile.id;

		const rights = await this.userDataService.getUserAccessRights();
		localStorage.setItem("getTokenFunctionClaims", Object.values(rights)[2]);
		localStorage.setItem("getTokenProductClaims", Object.values(rights)[0]);
		localStorage.setItem("getTokenScreenClaims", Object.values(rights)[1]);

		console.log('[Account Info] Done');

		return profile
	}

	// Logs in with JWT token after google authentication
	private async loginByGoogle(token: string): Promise<void> {

		this.authService.setToken(token);

		const profile = await this.getAccountInfo();

		// Checks password age if Password Login is enabled, otherwise login
		this.ngZone.run(() => {
			if (this.passwordLogin) {
				this.checkPasswordAge();
			} else {
				this.router.navigate(['/settings/myaccount']);
			}
		})
	}
}
